/**
 * <DeltaBadge current={r.revenue} previous={r.previous_revenue} />  -> "↗ +₹28 K · +6.2%" (green)
 *
 * Shows the change as ₹ (or units / points) alongside %, coloured by direction.
 *   current, previous   numbers; or pass delta (+ optional pct) directly when only those are known.
 *   kind     'money' (default) | 'number' | 'percent' (delta shown as percentage points, no %)
 *   invert   true when lower is better (returns, return rate): down is green, up is red.
 *   showPct  false to hide the % part.  size 'sm' | 'xs'.
 * previous 0 and current > 0 -> "New"; missing values -> "—"; no change -> "No change".
 * Also exports formatDelta(delta, kind) -> "+₹28 K" / "−12" / "+0.4 pts".
 */
import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { changeParts, number, shortMoney, signedPct } from './format';

export const formatDelta = (delta, kind = 'money') => {
  if (delta == null || Number.isNaN(Number(delta))) return '—';
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const abs = Math.abs(delta);
  if (kind === 'percent') return `${sign}${abs.toFixed(1)} pts`;
  if (kind === 'number') return `${sign}${number(abs)}`;
  return `${sign}${shortMoney(abs)}`;
};

const TONE = {
  good: 'bg-green-50 text-green-800 ring-green-200',
  bad: 'bg-red-50 text-red-800 ring-red-200',
  flat: 'bg-gray-100 text-gray-700 ring-gray-200',
  new: 'bg-sky-50 text-sky-800 ring-sky-200',
};

export const DeltaBadge = ({ current, previous, delta: deltaProp, pct: pctProp, kind = 'money', invert = false, showPct = true, size = 'sm', className = '', testId }) => {
  const parts = deltaProp != null
    ? { delta: deltaProp, pct: pctProp ?? null, isNew: false, direction: deltaProp > 0 ? 'up' : deltaProp < 0 ? 'down' : 'flat' }
    : changeParts(current, previous);
  const text = size === 'xs' ? 'text-[11px] px-1.5 py-0' : 'text-xs px-2 py-0.5';
  const base = `inline-flex items-center gap-0.5 whitespace-nowrap rounded-full font-medium tabular-nums ring-1 ring-inset ${text} ${className}`;
  if (parts.isNew) return <span className={`${base} ${TONE.new}`} data-testid={testId} title="No sales in the comparison period">New</span>;
  if (parts.direction === 'none') return <span className={`${base} ${TONE.flat}`} data-testid={testId} aria-label="No comparison available">—</span>;
  if (parts.direction === 'flat') return <span className={`${base} ${TONE.flat}`} data-testid={testId}>No change</span>;
  const up = parts.direction === 'up';
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const pctText = showPct && kind !== 'percent' && parts.pct != null ? signedPct(parts.pct) : '';
  return (
    <span className={`${base} ${good ? TONE.good : TONE.bad}`} data-testid={testId}
      aria-label={`${up ? 'Up' : 'Down'} ${formatDelta(Math.abs(parts.delta), kind).replace(/^\+/, '')}${pctText ? ` (${pctText})` : ''}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {formatDelta(parts.delta, kind)}{pctText ? ` · ${pctText}` : ''}
    </span>
  );
};

export default DeltaBadge;
